import './Settings.css';
import React, { useEffect, useState, useCallback } from 'react';
import Integrations from './Integrations';
import PlaysView from '../components/Plays/PlaysView';

const getCompanyId = (c) => c?.CompanyId || c?.companyId || c?.company_id || c?.id || '';

function fmtMoney(v) {
  if (v == null || isNaN(Number(v))) return '--';
  return '$' + Math.round(Number(v)).toLocaleString();
}

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const SETTINGS_TABS = [
  { key: 'goals', label: 'Goals' },
  { key: 'billing', label: 'Plan & billing' },
  { key: 'integrations', label: 'Integrations' },
  { key: 'team', label: 'Team' },
];

export default function Settings({ companyInfo, token, onNavigate, apiUrl }) {
  const [activeTab, setActiveTab] = useState('goals');
  const [annualGoal, setAnnualGoal]       = useState('');
  const [monthlyGoals, setMonthlyGoals]   = useState({});
  const [existingGoal, setExistingGoal]   = useState(null);
  const [loading, setLoading]             = useState(true);
  const [saving, setSaving]               = useState(false);
  const [saved, setSaved]                 = useState(false);
  const [savingMonthly, setSavingMonthly] = useState(false);
  const [savedMonthly, setSavedMonthly]   = useState(false);
  const [error, setError]                 = useState(null);

  const companyId = getCompanyId(companyInfo);

  const fetchGoal = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${apiUrl}/api/dashboard/goals/revenue?company=${companyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error('Error loading goal');
      const data = await res.json();
      setExistingGoal(data);
      if (data.annual_goal) {
        setAnnualGoal(String(Math.round(data.annual_goal)));
      }
      if (data.goals?.length > 0) {
        const mg = {};
        data.goals.forEach(g => {
          if (g.goal_month_number) {
            mg[g.goal_month_number] = String(Math.round(g.goal_value));
          }
        });
        setMonthlyGoals(mg);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [companyId, token, apiUrl]);

  useEffect(() => {
    fetchGoal();
  }, [fetchGoal]);

  const handleSave = async () => {
    const value = parseFloat(annualGoal.replace(/[^0-9.]/g, ''));
    if (!value || value <= 0) {
      setError('Enter a valid annual goal.');
      return;
    }
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const res = await fetch(`${apiUrl}/api/dashboard/goals/revenue`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          company: companyId,
          annual_goal: value,
          goal_type: 'manual',
        }),
      });
      if (!res.ok) throw new Error('Error saving goal');
      setSaved(true);
      await fetchGoal();
      await refreshCheckpoint();
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // Aviso previo si algun mes queda muy por debajo del resto (posible error
  // de tipeo, p.ej. "1" en vez de "10000" o un cero de menos). No bloquea:
  // el usuario confirma si el numero bajo es real (temporada baja, tienda
  // cerrada, etc). Umbral: mes < 25% del promedio de los DEMAS meses con
  // valor > 0.
  const LOW_MONTH_RATIO = 0.25;
  const findLowOutlierMonths = (numericGoals) => {
    const entries = Object.entries(numericGoals).filter(([, v]) => v > 0);
    if (entries.length < 2) return [];
    return entries
      .filter(([m, v]) => {
        const others = entries.filter(([om]) => om !== m).map(([, ov]) => ov);
        if (!others.length) return false;
        const avgOthers = others.reduce((a, b) => a + b, 0) / others.length;
        return v < avgOthers * LOW_MONTH_RATIO;
      })
      .map(([m]) => MONTH_NAMES[Number(m) - 1]);
  };

  const refreshCheckpoint = async () => {
    if (!companyId) return;
    try {
      await fetch(
        `${apiUrl}/api/dashboard/checkpoint/refresh?company=${encodeURIComponent(companyId)}`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (e) {
      // No bloquear el guardado si el refresh falla; el checkpoint se
      // recalculara solo al vencer el cache (24h).
    }
  };

  const handleSaveMonthly = async () => {
    // Solo se mandan los meses con valor puesto por el usuario. Un mes en
    // blanco NO se manda (antes se mandaba como $0 y pisaba el valor que
    // ya tenia ese mes, arruinando el promedio anual).
    const numericGoals = {};
    for (let m = 1; m <= 12; m++) {
      const rawStr = monthlyGoals[m];
      if (rawStr === undefined || rawStr === null || rawStr === '') continue;
      const raw = rawStr.replace(/[^0-9.]/g, '');
      if (raw === '') continue;
      numericGoals[m] = parseFloat(raw) || 0;
    }
    if (Object.keys(numericGoals).length === 0) {
      setError('Enter a value for at least one month.');
      return;
    }

    const lowMonths = findLowOutlierMonths(numericGoals);
    if (lowMonths.length > 0) {
      const proceed = window.confirm(
        `${lowMonths.join(', ')} ${lowMonths.length > 1 ? 'are' : 'is'} well below the rest of the months. ` +
        `Is that correct, or was it a typo?\n\nOK = save anyway. Cancel = review.`
      );
      if (!proceed) return;
    }

    setSavingMonthly(true);
    setError(null);
    setSavedMonthly(false);
    try {
      const res = await fetch(`${apiUrl}/api/dashboard/goals/revenue`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          company: companyId,
          monthly_goals: numericGoals,
          goal_type: 'manual',
        }),
      });
      if (!res.ok) throw new Error('Error saving monthly goals');
      setSavedMonthly(true);
      await fetchGoal();
      await refreshCheckpoint();
      setTimeout(() => setSavedMonthly(false), 3000);
    } catch (e) {
      setError(e.message);
    } finally {
      setSavingMonthly(false);
    }
  };

  // "Estimated" indicator criterion (revision round 2):
  // goal_type lives on EACH element of existingGoal.goals, not at the top
  // level of the GET response. Since "Current goal" shows annual_goal (the
  // sum of the year) and months can mix manual + estimated, we only show the
  // "Estimated" tag if ALL present goals are goal_type 'estimated'. If there
  // is a mix (or no goals array), no tag is shown.
  const isEstimatedGoal = Array.isArray(existingGoal?.goals)
    && existingGoal.goals.length > 0
    && existingGoal.goals.every(g => g?.goal_type === 'estimated');

  // Wording for the estimated-goal formula: prefer the backend-provided
  // goal_basis {months_used, growth_pct} from any estimated goal entry;
  // fall back to the generic "6 months / +10%" text if not exposed.
  const goalBasis = Array.isArray(existingGoal?.goals)
    ? (existingGoal.goals.find(g => g?.goal_type === 'estimated' && g?.goal_basis)?.goal_basis || null)
    : null;
  const estimatedMonths = goalBasis?.months_used || 6;
  const estimatedGrowth = goalBasis?.growth_pct != null ? goalBasis.growth_pct : 10;
  const estimatedFormulaText = `average of your last ${estimatedMonths} months of sales +${estimatedGrowth}%`;

  const monthlyPreview = (() => {
    const v = parseFloat(annualGoal.replace(/[^0-9.]/g, ''));
    return v > 0 ? Math.round(v / 12) : null;
  })();

  const CheckIcon = () => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M2.5 7l3 3 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  return (
    <div className="settings-page">
      <div className="settings-header">
        <div className="settings-header-left">
          <button
            className="settings-back-btn"
            onClick={() => onNavigate?.('checkpoint')}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M10 12L6 8l4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Revenue Checkpoint
          </button>
          <h1 className="settings-title">Settings</h1>
        </div>
      </div>

      <div className="settings-tabs">
        {SETTINGS_TABS.map((tabItem) => (
          <button
            key={tabItem.key}
            className={`settings-tab ${activeTab === tabItem.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tabItem.key)}
          >
            {tabItem.label}
          </button>
        ))}
      </div>

      {activeTab === 'goals' && (
      <div className="settings-body">
        <section className="settings-section">
          <div className="settings-section-header">
            <h2>Revenue Goal</h2>
            <p>Set your annual revenue target. UPSELLErate will track your progress monthly. Progress toward this goal is shown on Revenue Checkpoint.</p>
          </div>

          {loading ? (
            <div className="settings-loading">
              <div className="settings-spinner" />
              <span>Loading…</span>
            </div>
          ) : (
            <div className="settings-card">
              {existingGoal?.annual_goal && (
                <div className="settings-current-goal">
                  <span className="settings-current-label">
                    Current goal
                    {isEstimatedGoal && (
                      <span
                        className="settings-estimated-tag"
                        title={`Estimated goal: ${estimatedFormulaText}. Save a value below to override it.`}
                      >
                        Estimated
                      </span>
                    )}
                  </span>
                  <span className="settings-current-value">
                    {fmtMoney(existingGoal.annual_goal)} / year
                  </span>
                  <span className="settings-current-monthly">
                    {fmtMoney(existingGoal.annual_goal / 12)} / month
                  </span>
                  {isEstimatedGoal && (
                    <span className="settings-current-hint">
                      Estimated: {estimatedFormulaText} — save to override.
                    </span>
                  )}
                </div>
              )}

              <div className="settings-field">
                <label className="settings-label">Annual revenue goal</label>
                <div className="settings-input-wrapper">
                  <span className="settings-input-prefix">$</span>
                  <input
                    className="settings-input"
                    type="text"
                    inputMode="numeric"
                    placeholder="500,000"
                    value={annualGoal}
                    onChange={(e) => {
                      setSaved(false);
                      setError(null);
                      setAnnualGoal(e.target.value.replace(/[^0-9]/g, ''));
                    }}
                  />
                </div>

                {monthlyPreview && (
                  <p className="settings-field-hint">
                    {fmtMoney(monthlyPreview)} / month across 12 months
                  </p>
                )}
              </div>

              {error && (
                <div className="settings-error">{error}</div>
              )}

              <div className="settings-actions">
                <button
                  className="settings-save-btn"
                  onClick={handleSave}
                  disabled={saving || !annualGoal}
                >
                  {saving ? (
                    <><div className="settings-spinner-sm" /> Saving…</>
                  ) : saved ? (
                    <><CheckIcon /> Saved</>
                  ) : (
                    'Save goal'
                  )}
                </button>
              </div>
            </div>
          )}
        </section>

        {!loading && existingGoal?.goals?.length > 0 && (
          <section className="settings-section">
            <div className="settings-section-header">
              <h2>Monthly Breakdown</h2>
              <p>Adjust individual month targets. Changes here override the annual split.</p>
            </div>
            <div className="settings-card">
              <div className="settings-monthly-grid">
                {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                  <div key={m} className="settings-month-row">
                    <span className="settings-month-label">{MONTH_NAMES[m - 1]}</span>
                    <div className="settings-input-wrapper settings-month-input-wrapper">
                      <span className="settings-input-prefix">$</span>
                      <input
                        className="settings-input settings-month-input"
                        type="text"
                        inputMode="numeric"
                        value={monthlyGoals[m] || ''}
                        onChange={(e) => {
                          setSavedMonthly(false);
                          setError(null);
                          setMonthlyGoals(prev => ({
                            ...prev,
                            [m]: e.target.value.replace(/[^0-9]/g, ''),
                          }));
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="settings-actions">
                <button
                  className="settings-save-btn"
                  onClick={handleSaveMonthly}
                  disabled={savingMonthly}
                >
                  {savingMonthly ? (
                    <><div className="settings-spinner-sm" /> Saving…</>
                  ) : savedMonthly ? (
                    <><CheckIcon /> Saved</>
                  ) : (
                    'Save monthly goals'
                  )}
                </button>
              </div>
            </div>
          </section>
        )}
      </div>
      )}

      {activeTab === 'billing' && (
        <div className="settings-body">
          <section className="settings-section">
            <div className="settings-section-header">
              <h2>Plan &amp; billing</h2>
              <p>Manage your plan, Plays usage, and invoices.</p>
            </div>
            {/* Misma pantalla que el boton "Plans" del nav principal (decision
                de Alejo 2026-08-09): no un placeholder aparte, sino el
                componente real reutilizado. checkoutResult queda null aca
                porque el retorno del checkout de Shopify (billing_callback)
                siempre aterriza en la vista 'pricing' de App.js, no en
                Settings -- el banner de exito/error ya se ve ahi. */}
            <PlaysView token={token} checkoutResult={null} onDismissCheckoutResult={() => {}} />
          </section>
        </div>
      )}

      {activeTab === 'integrations' && (
        <div className="settings-body settings-body-integrations">
          <Integrations companyInfo={companyInfo} />
        </div>
      )}

      {activeTab === 'team' && (
        <div className="settings-body">
          <section className="settings-section">
            <div className="settings-section-header">
              <h2>Team</h2>
              <p>Invite teammates to collaborate on Revenue Plays and Checkpoint.</p>
            </div>
            <div className="settings-card settings-placeholder-card">
              <p className="settings-placeholder-text">
                Multi-user team access is a Pro feature and is coming soon.
              </p>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
